import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useVersionCheck } from '@/hooks/useVersionCheck';

const DISMISS_KEY = 'pwa_update_dismissed_at';
const DISMISS_DURATION = 60 * 60 * 1000; // 1 hour

export function UpdateNotification() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { t } = useLanguage();
  
  // Initialize version checking (polling + visibility detection)
  useVersionCheck();

  useEffect(() => {
    const checkDismissed = () => {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt) {
        const elapsed = Date.now() - parseInt(dismissedAt, 10);
        if (elapsed < DISMISS_DURATION) {
          return true;
        }
        localStorage.removeItem(DISMISS_KEY);
      }
      return false;
    };

    // Listen for custom update event from version check or SW
    const handleUpdateAvailable = () => {
      if (!checkDismissed()) {
        setShowUpdate(true);
      }
    };

    window.addEventListener('sw:update-available', handleUpdateAvailable);

    // Also listen for controller change (new SW activated)
    const handleControllerChange = () => {
      if (!checkDismissed()) {
        setShowUpdate(true);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    return () => {
      window.removeEventListener('sw:update-available', handleUpdateAvailable);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, []);

  const handleUpdate = () => {
    setIsUpdating(true);
    // Clear stored version so it gets updated after reload
    localStorage.removeItem('cashee_version_buildTime');
    // Small delay for visual feedback
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShowUpdate(false);
  };

  return (
    <AnimatePresence>
      {showUpdate && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.9 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-20 right-4 left-4 sm:left-auto sm:w-80 z-50"
        >
          <motion.div 
            className="bg-primary text-primary-foreground rounded-xl shadow-lg p-4 relative"
            animate={{ 
              boxShadow: ['0 10px 40px -10px hsl(var(--primary) / 0.3)', '0 10px 40px -10px hsl(var(--primary) / 0.5)', '0 10px 40px -10px hsl(var(--primary) / 0.3)']
            }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-primary-foreground/20 transition-colors"
              aria-label={t('common.close')}
              disabled={isUpdating}
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-start gap-3 pr-6">
              <div className="p-2 bg-primary-foreground/20 rounded-lg shrink-0">
                <motion.div
                  animate={isUpdating ? { rotate: 360 } : {}}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw className="w-5 h-5" />
                </motion.div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm">
                  {t('pwa.update.title')}
                </h4>
                <p className="text-xs opacity-90 mt-0.5">
                  {t('pwa.update.description')}
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleUpdate}
              variant="secondary"
              size="sm"
              className="w-full mt-3 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              disabled={isUpdating}
            >
              {isUpdating ? t('pwa.update.updating') : t('pwa.update.button')}
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}